import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Editor from 'react-froala-wysiwyg';
import { useIntl } from 'react-intl';

import RegisterFieldReplacements from './buttons/field-replacement';
import EnglishOnly from './component.english';
import style from './component.module.less';
import { FIELD_REPLACEMENT_SETS } from './constants';
import { translateTokens, translateTokensToLanguage } from './helpers';
import { useConfig } from './hooks';
import { useLocale } from '../../intl/hooks';
import IntlProvider from '../../intl/intl-provider';
import AuthenticatedLocaleContextProvider from '../../intl/locale-context-provider/authenticated-locale-context-provider';

import 'froala-editor/css/froala_editor.css';

export const SSEditor = ({ isHTMLOnly = false, onChange, placeholder, replacementSet, value }) => {
  const intl = useIntl();
  const { messages } = intl;
  const previousIntl = useRef(intl);
  const { locale } = useLocale();
  const config = useConfig(locale, placeholder, isHTMLOnly);
  const [error, setError] = useState();
  const [model, setModel] = useState(translateTokens(value, intl));
  const previousModel = useRef(model);

  const [firstMessageKey] = Object.keys(intl.messages);

  useEffect(() => {
    return () => {
      previousModel.current = model;
    };
  }, [model]);
  useEffect(() => {
    setModel((currentModel) => {
      return translateTokensToLanguage(currentModel, previousIntl.current, intl);
    });

    return () => {
      previousIntl.current = intl;
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, previousIntl, setModel]);

  /**
   * Have to use an inline style block here to override soem of the froala basic styles.
   * Using our own less files would append a hash onto the generated class names.
   *
   * .fr-second-toolbar hides the "Powered By Froala" banner at the bottom of the editor.
   *
   * .fr-wrapper makes sure the bottom edge of the editor is rounded like it would be if
   * we weren't hiding the "Powered By Froala" footer banner mentioned up above.
   *
   * .fr-dropdown-list li makes all of the dropdowns in the editor's toolbar expand vertically,
   * this overrides a ss base style of inline-block for li's
   *
   * .fr-iframe sets the height of the editor's text area
   */
  return (
    <div data-ignore-root-event-listeners data-ignore-root-focus='1'>
      <style>
        {`.fr-second-toolbar {
          display: none;
        }

        .fr-box {
          height: 100%;
        }

        .fr-wrapper {
          border-bottom-left-radius: 2px;
          border-bottom-right-radius: 2px;
          min-height: 200px;
          height: 80%;
        }

        .fr-toolbar.fr-top {
          border-radius: 2px;
        }

        .fr-dropdown-list li {
          display: block;
        }`}
      </style>
      <RegisterFieldReplacements key={`${messages[firstMessageKey]}-0`} onXssError={setError} replacementSet={replacementSet} />
      {error ? <div className={style.error}>{error}</div> : null}
      <Editor
        key={messages[firstMessageKey]}
        config={config}
        model={model !== null ? model : previousModel.current}
        onModelChange={setModel}
      />
      <AuthenticatedLocaleContextProvider>
        <IntlProvider>
          <EnglishOnly intlFrom={intl} model={model} onChange={onChange} />
        </IntlProvider>
      </AuthenticatedLocaleContextProvider>
    </div>
  );
};

SSEditor.propTypes = {
  isHTMLOnly: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  replacementSet: PropTypes.oneOf(Object.values(FIELD_REPLACEMENT_SETS)).isRequired,
  value: PropTypes.string.isRequired,
};
